import React, { useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { post } from 'axios'
import {
    Button, Dimmer, Dropdown,
    Form, Icon, Label, Loader, Modal, Segment,
} from 'semantic-ui-react'
import { URL } from '../../../store/path'
import { setAlertModalState } from '../../common/data/actions'
import { extractAxiosError, extractErrorMessage } from '../data/errorMsg'
import { uploaderTypeOptions } from '../data/model'

const UploaderModal = ({ open, onClose, onUploaded }) => {
    const dispatch = useDispatch()
    const fileInputRef = useRef(null)
    const [type, setType] = useState('Segmap')
    const [fileObj, setFileObj] = useState(null)
    const [fileName, setFileName] = useState('')
    const [isDownloading, setIsDownloading] = useState(false)
    const [isUploading, setIsUploading] = useState(false)

    const busy = isUploading || isDownloading

    const resetState = () => {
        setType('Segmap')
        setFileObj(null)
        setFileName('')
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const handleClose = () => {
        if (busy) return
        resetState()
        onClose()
    }

    const handleFileChange = (e) => {
        const file = e.target.files && e.target.files[0]
        if (!file) return
        const ext = file.name.split('.').pop().toLowerCase()
        if (ext !== 'xlsx' && ext !== 'xls') {
            dispatch(setAlertModalState({
                isOpen: true,
                type: 'error',
                size: 'mini',
                title: 'Invalid File',
                msg: 'Only .xlsx or .xls files are allowed.',
            }))
            e.target.value = ''
            return
        }
        setFileObj(file)
        setFileName(file.name)
    }

    const clearFile = () => {
        setFileObj(null)
        setFileName('')
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const downloadTemplate = async () => {
        if (!type) {
            dispatch(setAlertModalState({
                isOpen: true,
                type: 'error',
                size: 'mini',
                title: 'Validation Error',
                msg: 'Please select a Type to download template.',
            }))
            return
        }
        setIsDownloading(true)
        try {
            const response = await post(
                URL + '/upload/master/TemplateDownload',
                { type },
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('user')}`,
                        'X-RLB-DMS-ACC': localStorage.getItem('acc'),
                        'Content-Type': 'application/json',
                    },
                    responseType: 'blob',
                }
            )
            const contentType = response.headers['content-type'] || ''
            if (contentType.includes('application/json')) {
                const text = await response.data.text()
                let json = {}
                try { json = JSON.parse(text) } catch (e) { json = { data: text } }
                dispatch(setAlertModalState({
                    isOpen: true,
                    type: 'error',
                    size: 'mini',
                    title: 'Download Error',
                    msg: extractErrorMessage(json, 'Failed to download template.'),
                }))
                return
            }
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            })
            const downloadUrl = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.style.display = 'none'
            a.href = downloadUrl
            a.download = `${type}_template.xlsx`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(downloadUrl)
        } catch (error) {
            const msg = await extractAxiosError(error, 'Failed to download template.')
            dispatch(setAlertModalState({
                isOpen: true,
                type: 'error',
                size: 'mini',
                title: 'Download Error',
                msg,
            }))
        } finally {
            setIsDownloading(false)
        }
    }

    const uploadFile = async () => {
        if (!type) {
            dispatch(setAlertModalState({
                isOpen: true,
                type: 'error',
                size: 'mini',
                title: 'Validation Error',
                msg: 'Please select a Type.',
            }))
            return
        }
        if (!fileObj) {
            dispatch(setAlertModalState({
                isOpen: true,
                type: 'error',
                size: 'mini',
                title: 'Validation Error',
                msg: 'Please select a file to upload.',
            }))
            return
        }
        const formData = new FormData()
        formData.append('file', fileObj)
        formData.append('parent_grp', localStorage.getItem('acc') || '')
        formData.append('user_email', localStorage.getItem('userName') || '')
        formData.append('type', type.toLowerCase())

        setIsUploading(true)
        try {
            const response = await post(
                URL + '/upload/master/excel-upload',
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('user')}`,
                        'X-RLB-DMS-ACC': localStorage.getItem('acc'),
                        'Content-Type': 'multipart/form-data',
                    },
                }
            )
            const data = response.data || {}
            if (data.type === 'error') {
                dispatch(setAlertModalState({
                    isOpen: true,
                    type: 'error',
                    size: 'mini',
                    title: 'Upload Error',
                    msg: extractErrorMessage(data, 'Upload failed.'),
                }))
            } else {
                dispatch(setAlertModalState({
                    isOpen: true,
                    type: 'success',
                    size: 'mini',
                    title: 'Success',
                    msg: data.message || data.msg || data.data || 'File uploaded successfully.',
                }))
                resetState()
                if (onUploaded) onUploaded()
                onClose()
            }
        } catch (error) {
            const msg = await extractAxiosError(error, 'Upload failed.')
            dispatch(setAlertModalState({
                isOpen: true,
                type: 'error',
                size: 'mini',
                title: 'Upload Error',
                msg,
            }))
        } finally {
            setIsUploading(false)
        }
    }

    return (
        <Modal open={open} size='tiny' onClose={handleClose} closeOnDimmerClick={false}>
            <Modal.Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', fontSize: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon name='cloud upload' style={{ color: '#2185d0', margin: 0, fontSize: '16px' }} />
                    <span style={{ fontWeight: 600, fontSize: '15px', color: '#1f2d3d' }}>Upload Master Data</span>
                </div>
                <Icon
                    name='close'
                    link
                    onClick={handleClose}
                    style={{ color: '#999', fontSize: '14px', cursor: busy ? 'not-allowed' : 'pointer' }}
                />
            </Modal.Header>
            <Modal.Content style={{ padding: '22px' }}>
                {busy && (
                    <Dimmer active inverted>
                        <Loader indeterminate>
                            {isUploading ? 'Uploading File...' : 'Downloading Template...'}
                        </Loader>
                    </Dimmer>
                )}
                <Form>
                    <Form.Field>
                        <label>Type <font color='red'>*</font></label>
                        <Dropdown
                            placeholder='Select Type'
                            selection
                            search
                            fluid
                            options={uploaderTypeOptions}
                            value={type}
                            onChange={(e, { value }) => setType(value)}
                        />
                    </Form.Field>

                    <Segment
                        style={{
                            background: '#f7fbff',
                            border: '1px solid #d6e6f5',
                            padding: '14px 16px',
                            marginTop: '14px',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                                <div style={{ fontWeight: 600, color: '#1a3a5a' }}>Step 1 &middot; Template</div>
                                <div style={{ fontSize: '12px', color: '#5a7a99', marginTop: '2px' }}>
                                    Download the Excel template for the selected type.
                                </div>
                            </div>
                            <Button
                                type='button'
                                color='teal'
                                size='small'
                                icon
                                labelPosition='left'
                                onClick={downloadTemplate}
                                disabled={busy}
                            >
                                <Icon name='download' />
                                Get Template
                            </Button>
                        </div>
                    </Segment>

                    <Segment
                        style={{
                            background: '#f7fff9',
                            border: '1px solid #d6f0de',
                            padding: '14px 16px',
                            marginTop: '12px',
                        }}
                    >
                        <div style={{ fontWeight: 600, color: '#1a5a32', marginBottom: '10px' }}>
                            Step 2 &middot; Upload Excel File
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <Button
                                as='label'
                                htmlFor='uploader-file-input'
                                type='button'
                                size='small'
                                icon
                                labelPosition='left'
                                color='blue'
                                basic
                                style={{ margin: 0 }}
                            >
                                <Icon name='file excel outline' />
                                {fileObj ? 'Change File' : 'Choose File'}
                            </Button>
                            <input
                                id='uploader-file-input'
                                ref={fileInputRef}
                                type='file'
                                accept='.xlsx,.xls'
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                            {fileObj ? (
                                <Label
                                    color='green'
                                    basic
                                    style={{
                                        maxWidth: '320px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <Icon name='file excel' />
                                    <span title={fileName}>{fileName}</span>
                                    <Icon name='delete' link onClick={clearFile} style={{ marginLeft: '6px' }} />
                                </Label>
                            ) : (
                                <span style={{ color: '#8a8a8a', fontSize: '13px' }}>
                                    No file selected (.xlsx or .xls)
                                </span>
                            )}
                        </div>
                    </Segment>
                </Form>
            </Modal.Content>
            <Modal.Actions style={{ padding: '12px 22px' }}>
                <Button
                    type='button'
                    basic
                    onClick={handleClose}
                    disabled={busy}
                >
                    Cancel
                </Button>
                <Button
                    type='button'
                    color='blue'
                    icon
                    labelPosition='left'
                    onClick={uploadFile}
                    disabled={!fileObj || busy}
                >
                    <Icon name='upload' />
                    Upload
                </Button>
            </Modal.Actions>
        </Modal>
    )
}

export default UploaderModal
